import React from "react";

import { getThemeAssetUrl } from "../utils/urlHelpers";
import {
  Box,
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Gavel as LegalIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => ({
  backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${getThemeAssetUrl('/build/grey-building.jpg')}')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: theme.spacing(10, 0, 6, 0),
  textAlign: 'center',
  marginBottom: theme.spacing(6),
      marginTop: theme.spacing(2),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));



const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: "8px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:before": {
    display: "none", // Remove the default divider
  },
}));

const AccordionQuestionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: "#f5f7fa",
  padding: theme.spacing(1, 2),
  "&.Mui-expanded": {
    backgroundColor: "#e8f4fd",
  },
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1, 0),
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#0e3158",
  fontSize: '1.05rem',
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.8,
  color: "#333",
}));

const CategoryBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.25rem",
  marginBottom: theme.spacing(2),
  color: "#0e3158",
  borderBottom: "2px solid #f0b94d",
  paddingBottom: theme.spacing(1),
  display: "inline-block",
}));

const CtaSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: '#f5f7fa',
  borderRadius: '8px',
}));

// Organize FAQs by practice area categories
const faqCategories = [
  {
    category: "Family Law",
    faqs: [
      {
        question: "How is child custody decided during a divorce?",
        answer: `In Alberta, decisions about child custody (parenting) and access (parenting time) are made based on the best interests of the child. There is no automatic formula – instead, the courts (or the parents in a settlement) will consider factors like each parent's ability to care for the child, the child's routine and stability, in some cases the child's own wishes (depending on age/maturity), and whether each parent can support the child's emotional, physical, and educational needs.

The term "custody" is now often referred to as decision-making responsibility (who makes major decisions for the child) and "access" as parenting time. Many parents opt for joint decision-making and a shared parenting schedule if workable.`
      },
      {
        question: "How long does a divorce take in Alberta?",
        answer: "The timeline for a divorce in Alberta varies depending on the complexity and whether it's contested or uncontested. An uncontested divorce where both parties agree on all terms can typically be completed in 3-6 months. Contested divorces requiring court appearances can take 1-2 years or longer. The mandatory one-year separation period must be completed before a divorce can be finalized."
      }
    ]
  },
  {
    category: "Personal Injury",
    faqs: [
      {
        question: "What should I do immediately after a car accident?",
        answer: `If you're involved in a car accident, your health and safety are the top priority. First, check for injuries – seek medical attention right away if needed (even for seemingly minor injuries, it's good to get checked out, and it creates a medical record).

Second, if it's safe, collect information at the scene: take photos of vehicle damage, the accident scene, and any visible injuries; exchange contact and insurance details with the other driver(s); and get names and phone numbers of any witnesses.

Third, report the accident – call the police if the damage is above a certain threshold (in Alberta, if damage seems over $2,000 or if there are injuries, the police should be notified).`
      },
      {
        question: "How much is my injury claim worth?",
        answer: "Each personal injury claim is unique, and its value depends on several factors including the severity of your injuries, long-term prognosis, impact on your ability to work, medical expenses, and non-economic damages like pain and suffering. Alberta has caps on minor injury claims, but more serious injuries can result in significantly higher compensation. A thorough assessment by a personal injury lawyer is needed to provide an accurate estimate."
      }
    ]
  },
  {
    category: "Real Estate",
    faqs: [
      {
        question: "What are the closing costs when buying a home?",
        answer: "When purchasing a home in Alberta, typical closing costs include legal fees and disbursements (approximately $1,000-1,500), land transfer tax (in Alberta this is a relatively low registration fee based on the property value), property tax adjustments, home inspection fees ($300-500), property insurance, mortgage insurance if applicable, and utility setup fees. Budget approximately 1.5-4% of the purchase price for closing costs."
      },
      {
        question: "What happens if there are issues found during the home inspection?",
        answer: "If a home inspection reveals significant issues, you generally have several options: renegotiate the purchase price to reflect repair costs, request that the seller make the repairs before closing, proceed with the purchase as is (potentially at your own risk), or walk away from the deal if your purchase agreement includes a home inspection condition. The appropriate action depends on the severity of the issues and your purchase agreement terms."
      }
    ]
  },
  {
    category: "Immigration",
    faqs: [
      {
        question: "How can I sponsor my spouse to come to Canada?",
        answer: "To sponsor your spouse for Canadian permanent residence, you must be a Canadian citizen or permanent resident, at least 18 years old, and demonstrate your ability to financially support them. The process involves submitting an application to IRCC with proof of your relationship (marriage certificate, photos, communication records, etc.), financial documentation, and various forms. Processing times typically range from 12-24 months depending on the applicant's country of residence."
      },
      {
        question: "What is Express Entry and how does it work?",
        answer: "Express Entry is Canada's main system for managing skilled worker immigration applications. It's a points-based system that ranks candidates based on factors like age, education, language proficiency, work experience, and Canadian connections. Those with the highest scores receive Invitations to Apply (ITAs) for permanent residence through regular draws. The process involves creating an Express Entry profile, receiving an ITA if eligible, then submitting a complete application to IRCC within 60 days."
      }
    ]
  },
  {
    category: "Corporate Law",
    faqs: [
      {
        question: "Should I incorporate my small business?",
        answer: "Deciding whether to incorporate depends on several factors, such as the size and nature of your business, your goals, and your tolerance for administrative work. The advantages of incorporation include: limited liability (personal assets are generally protected from business debts), potential tax benefits, and enhanced credibility. However, incorporation comes with costs and additional administrative responsibilities. Many small businesses consider incorporation once their revenue and risk level increase."
      },
      {
        question: "What's the difference between a shareholder and a director?",
        answer: "Shareholders own the corporation through their shares, while directors manage its affairs and make business decisions. Shareholders have limited liability and their primary rights include voting on major corporate decisions, receiving dividends, and electing directors. Directors have fiduciary duties to act in the best interest of the corporation and can face personal liability for certain corporate actions. One person can be both a shareholder and director, which is common in small businesses."
      }
    ]
  }
];

function PracticeAreasFAQ() {
  return (
    <>
      <SeoMetaTags
        title="Practice Areas FAQ - Common Legal Questions | Sentry Law"
        description="Find answers to common questions about our practice areas including personal injury, corporate law, immigration, and more. Get personalized legal advice."
        slug="practice-areas-faq"
      />
      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">
          <PageTitle variant="h1">
            Practice Areas FAQ
          </PageTitle>
          <Typography variant="subtitle1" sx={{ maxWidth: 800, mx: 'auto', mb: 3, lineHeight: 1.7 }}>
            Find answers to common questions about our different practice areas.
            For personalized legal advice, please contact us for a consultation.
          </Typography>
        </Container>
      </PageHeader>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {/* FAQ Categories */}
        {faqCategories.map((category, categoryIndex) => (
          <CategoryBox key={categoryIndex}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LegalIcon sx={{ color: '#0e3158', mr: 1 }} />
              <CategoryTitle variant="h5">
                {category.category}
              </CategoryTitle>
            </Box>
            
            {category.faqs.map((faq, faqIndex) => (
              <StyledAccordion key={faqIndex} defaultExpanded={faqIndex === 0}>
                <AccordionQuestionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#0e3158" }} />}
                  aria-controls={`faq-content-${categoryIndex}-${faqIndex}`}
                  id={`faq-header-${categoryIndex}-${faqIndex}`}
                >
                  <QuestionText>
                    {faq.question}
                  </QuestionText>
                </AccordionQuestionSummary>
                <AccordionDetails sx={{ py: 2, px: 3 }}>
                  <AnswerText>
                    {faq.answer.split('\n\n').map((paragraph, i) => (
                      <React.Fragment key={i}>
                        {paragraph}
                        {i < faq.answer.split('\n\n').length - 1 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </AnswerText>
                </AccordionDetails>
              </StyledAccordion>
            ))}
          </CategoryBox>
        ))}

        {/* CTA Section */}
        <CtaSection>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700, color: '#0e3158' }}>
            Have More Questions?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            If you don't see your question here or need personalized legal advice for your specific situation,
            our experienced team is ready to help. Contact us for a consultation.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact-us"
              sx={{ 
                bgcolor: '#f0b94d', 
                color: '#0e3158', 
                fontWeight: 600,
                '&:hover': { bgcolor: '#d9a843' }, 
                px: 3, 
                py: 1.5 
              }}
            >
              Schedule Free Consultation
            </Button>
            <Button
              variant="outlined"
              href="tel:+16045551234"
              sx={{ 
                borderColor: '#0e3158', 
                color: '#0e3158',
                fontWeight: 600,
                '&:hover': { borderColor: '#0a2548', bgcolor: 'rgba(14, 49, 88, 0.05)' }, 
                px: 3, 
                py: 1.5 
              }}
            >
              Call (604) 555-1234
            </Button>
          </Box>
        </CtaSection>
      </Container>
    </>
  );
}

export default PracticeAreasFAQ;